import React from "react";
import { useSelector } from "react-redux";
import { ChartData } from "chart.js";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { verticalChartOptions } from "../../../../../utils/charts/chartOptions";
import ChartLegend from "../../ChartLegend/ChartLegend";

import theme from "../../../../../components/styles/theme/theme";
import { RootState } from "../../../../../store/store";
import { isRegion } from "../../../../../config";
import { autoDenominate } from "../../../../../utils/charts/formatChartValues";

const EconomicValueChainStageChart: React.FC = () => {
  const { t } = useTranslation();

  const {
    statistics: { statisticsForSelectedCountry },
  } = useSelector((state: RootState) => state);

  const vcl = statisticsForSelectedCountry?.va_valueChainLink;
  const rawValues = vcl ? [
    vcl.downstream_suppliers_and_outlets,
    vcl.theSystem_theCocaColaSystem,
    vcl.downstream_Vat,
    vcl.upstream_suppliers_and_outlets,
    vcl.downstream_suppliers_and_outlets_suppliers,
    vcl.upstream_suppliers_and_outlets_suppliers,
  ] : [];
  const { converted, unit: displayUnit } = autoDenominate(rawValues, vcl?.display_values_in || "USD million");

  const v = (i: number) => converted[i] ?? null;
  const statisticsDataArray = [
    [null, null, v(0)],
    [null, v(1), null],
    [null, null, v(2)],
    [v(3), null, null],
    [null, null, v(4)],
    [v(5), null, null],
  ].map((row) => row.map((value) => (value === 0 ? null : value)));

  const labels = [
    t("charts.upstream_label"),
    t("charts.system_label"),
    t("charts.downstream_label"),
  ];
// @ts-ignore
  const data: ChartData<"bar"> = {
    labels,
    datasets: [
      {
        label: (isRegion.usa ? t("charts.outlets_label_usa") : t("charts.outlets_label")) || "",
        data: statisticsDataArray[0],
        backgroundColor: theme.palette.primary["700"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },
      {
        label: t("charts.system_label") || "",
        data: statisticsDataArray[1],
        backgroundColor: theme.palette.secondary["600"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },
      {
        label: (isRegion.usa ? t("charts.consumer_sales_tax_label") : t("charts.vat_label")) || "",
        data: statisticsDataArray[2],
        backgroundColor: theme.palette.primary["500"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },
      {
        label: t("charts.first_tier_suppliers_label") || "",
        data: statisticsDataArray[3],
        backgroundColor: theme.palette.secondary["200"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },

      {
        label: (isRegion.usa ? t("charts.outlets_suppliers_label_usa") : t("charts.outlets_suppliers_label")) || "",
        data: statisticsDataArray[4],
        backgroundColor: theme.palette.primary["300"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },

      {
        label: t("charts.suppliers_suppliers_label") || "",
        data: statisticsDataArray[5],
        backgroundColor: theme.palette.secondary["50"],
        minBarLength: 20,
        maxBarThickness: 80,
        borderWidth: 1,
        borderColor: theme.palette.common.white,
      },
    ],
  };

  return (
    <>
      <Bar options={verticalChartOptions("economic_value_added")} data={data} />
      <ChartLegend data={data} />
    </>
  );
};

export default EconomicValueChainStageChart;
